import { Bot, Plus, ArrowRight } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { useAgents } from '../hooks/useAgents'
import { agentService } from '../services/api'
import { formatDateDetailed } from '../utils/format'
import { Loading } from '../components/common/Loading'
import { EmptyState } from '../components/common/EmptyState'

export function Dashboard() {
  const { data: agents, isLoading: agentsLoading } = useAgents()
  const navigate = useNavigate()
  
  // * Fetch active agents (instances) for the first block
  const { data: activeAgents } = useQuery(
    'activeAgents',
    agentService.getActiveAgents,
    {
      refetchInterval: 30000 // * Refresh every 30 seconds
    }
  )

  // Calculer les statistiques pour les instances d'agents
  const totalActiveAgents = activeAgents?.length || 0
  const activeCount = activeAgents?.filter(agent => agent.activeStatus === 'active').length || 0
  const dormantCount = activeAgents?.filter(agent => agent.activeStatus === 'dormant').length || 0
  const inactiveCount = activeAgents?.filter(agent => agent.activeStatus === 'inactive').length || 0

  // Calculer les statistiques pour les modèles d'agents
  const totalAgents = agents?.length || 0
  const runningAgents = agents?.filter(agent => agent.status === 'running').length || 0
  const stoppedAgents = agents?.filter(agent => agent.status === 'stopped').length || 0
  const createdAgents = agents?.filter(agent => agent.status === 'created').length || 0

  // Trier les agents par date de mise à jour (plus récents en premier)
  const recentAgents = agents
    ? [...agents]
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .slice(0, 5)
    : []

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'À l\'instant'
    if (diffMins < 60) return `Il y a ${diffMins} min`
    if (diffHours < 24) return `Il y a ${diffHours}h`
    if (diffDays < 7) return `Il y a ${diffDays}j`
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
  }

  if (agentsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 spinner"></div>
      </div>
    )
  }

  return (
    <div className="page-content relative min-h-screen">
      {/* Image de robot en arrière-plan */}
      <div className="background-image">
        <img src="/logo.jpg" alt="Robot background" />
      </div>
      
      {/* Contenu au premier plan */}
      <div className="content-wrapper">
      {/* Header */}
      <header className="mb-10">
        <div className="flex flex-col gap-6 mb-8">
          <div>
            <h1 className="text-3xl font-extrabold mb-2 tracking-tight tab_title" style={{ lineHeight: '1.2' }}>
              Accueil
            </h1>
            <p className="text-sm font-medium text-secondary">
              Vue d'ensemble de vos agents
            </p>
          </div>
        </div>
      </header>

      {/* Bloc Mes instances d'agents */}
      <section className="mb-10">
        <h2 className="text-2xl font-bold mb-4 tracking-tight text-primary">Mes instances d'agents</h2>
        <div 
          className="grid grid-cols-4 gap-4 items-center px-4 py-3 stat-card w-full cursor-pointer hover:bg-card-hover transition-colors"
          onClick={() => navigate('/instances/agents')}
        >
          <div className="col-span-1 min-w-0">
            <span className="text-sm font-medium text-tertiary whitespace-nowrap">Actifs: <span className="text-lg font-semibold text-primary">{activeCount}</span> <span className="text-xs text-success">({Math.round((activeCount / totalActiveAgents) * 100) || 0}%)</span></span>
          </div>
          <div className="col-span-1 min-w-0">
            <span className="text-sm font-medium text-tertiary whitespace-nowrap">Dormants: <span className="text-lg font-semibold text-primary">{dormantCount}</span> <span className="text-xs text-warning">({Math.round((dormantCount / totalActiveAgents) * 100) || 0}%)</span></span>
          </div>
          <div className="col-span-1 min-w-0">
            <span className="text-sm font-medium text-tertiary whitespace-nowrap">Inactifs: <span className="text-lg font-semibold text-primary">{inactiveCount}</span> <span className="text-xs text-error">({Math.round((inactiveCount / totalActiveAgents) * 100) || 0}%)</span></span>
          </div>
          <div className="col-span-1 min-w-0">
            <span className="text-sm font-medium text-tertiary whitespace-nowrap">Total: <span className="text-lg font-semibold text-primary">{totalActiveAgents}</span></span>
          </div>
        </div>
      </section>

      {/* Bloc Mes modèles d'agents */}
      <section className="mb-10">
        <h2 className="text-2xl font-bold mb-4 tracking-tight text-primary">Mes modèles d'agents</h2>
        <div 
          className="grid grid-cols-4 gap-4 items-center px-4 py-3 stat-card w-full cursor-pointer hover:bg-card-hover transition-colors"
          onClick={() => navigate('/agents')}
        >
          <div className="col-span-1 min-w-0">
            <span className="text-sm font-medium text-tertiary whitespace-nowrap">En cours: <span className="text-lg font-semibold text-primary">{runningAgents}</span> <span className="text-xs text-success">({Math.round((runningAgents / totalAgents) * 100) || 0}%)</span></span>
          </div>
          <div className="col-span-1 min-w-0">
            <span className="text-sm font-medium text-tertiary whitespace-nowrap">Arrêtés: <span className="text-lg font-semibold text-primary">{stoppedAgents}</span> <span className="text-xs text-warning">({Math.round((stoppedAgents / totalAgents) * 100) || 0}%)</span></span>
          </div>
          <div className="col-span-1 min-w-0">
            <span className="text-sm font-medium text-tertiary whitespace-nowrap">Créés: <span className="text-lg font-semibold text-primary">{createdAgents}</span> <span className="text-xs text-tertiary">({Math.round((createdAgents / totalAgents) * 100) || 0}%)</span></span>
          </div>
          <div className="col-span-1 min-w-0">
            <span className="text-sm font-medium text-tertiary whitespace-nowrap">Total: <span className="text-lg font-semibold text-primary">{totalAgents}</span></span>
          </div>
        </div>
      </section>

      {/* Agents récents */}
      <section className="agent-list">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-6 py-6 list-header">
          <div>
            <h2 className="text-xl font-bold mb-2 tracking-tight text-primary">
              Agents récents
            </h2>
            <p className="text-xs font-medium text-secondary">
              Dernière activité de vos agents
            </p>
          </div>
          <Link
            to="/agents"
            className="action-link text-sm font-semibold px-3 py-1.5 rounded-lg hover:bg-robot-blue-light transition-all self-start"
          >
            <span>Voir tout</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div>
          {recentAgents.length === 0 ? (
            <EmptyState
              icon={Bot}
              title="Aucun agent"
              description="Créez votre premier agent pour commencer."
              action={
                <Link
                  to="/agents/new"
                  className="button button--primary"
                >
                  <Plus className="w-5 h-5" />
                  <span>Créer un agent</span>
                </Link>
              }
            />
          ) : (
            recentAgents.map((agent) => (
              <div key={agent.id} className="agent-list-item flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
                <div className="flex items-center gap-5 flex-1 min-w-0">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0 ${agent.status === 'running' ? 'icon-status-running' : 'icon-status-stopped'}`}>
                    <Bot className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2.5 mb-2">
                      <p className="text-base font-bold tracking-tight truncate text-primary">
                        {agent.name}
                      </p>
                      <span className={`inline-flex px-2.5 py-1 text-xs font-bold rounded-full shadow-sm flex-shrink-0 ${
                        agent.status === 'running' ? 'badge-success' : 
                        agent.status === 'stopped' ? 'badge-inactive' : 
                        'badge-warning'
                      }`}>
                        {agent.status === 'running' ? 'Actif' : 
                         agent.status === 'stopped' ? 'Arrêté' : 
                         agent.status === 'created' ? 'Créé' : 
                         agent.status === 'failed' ? 'Erreur' : agent.status}
                      </span>
                    </div>
                    <p className="text-sm font-medium mb-2 truncate text-secondary">
                      {agent.description || 'Aucune description'}
                    </p>
                    <p className="text-xs font-medium text-tertiary">
                      Dernière mise à jour: {formatDate(agent.updatedAt)}
                    </p>
                  </div>
                </div>
                <Link
                  to={`/agents/${agent.id}/edit`}
                  className="action-link text-sm font-semibold px-4 py-2 rounded-lg hover:bg-robot-blue-light transition-all self-start flex-shrink-0"
                >
                  <span>Modifier</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))
          )}
        </div>
      </section>
      </div>
    </div>
  )
}
